/**
 * Risk Entity - Constants
 * Configuration values, enums, and static data for Risk domain
 */

/**
 * Risk Categories
 */
export const RISK_CATEGORIES = [
  { value: 'Strategic', label: 'Strategic', icon: 'Target', color: '#64748B' },
  { value: 'Operational', label: 'Operational', icon: 'Settings', color: '#3B82F6' },
  { value: 'Financial', label: 'Financial', icon: 'DollarSign', color: '#10B981' },
  { value: 'Compliance', label: 'Compliance', icon: 'Shield', color: '#F59E0B' },
  { value: 'IT/Cyber', label: 'IT/Cyber', icon: 'Lock', color: '#EF4444' },
  { value: 'Reputational', label: 'Reputational', icon: 'Award', color: '#EC4899' },
  { value: 'Third-Party', label: 'Third-Party', icon: 'Users', color: '#8B5CF6' },
  { value: 'Environmental', label: 'Environmental', icon: 'Leaf', color: '#14B8A6' },
  { value: 'Health & Safety', label: 'Health & Safety', icon: 'Heart', color: '#F43F5E' },
];

/**
 * Risk Levels with scoring ranges
 */
export const RISK_LEVELS = {
  CRITICAL: {
    value: 'Critical',
    label: 'Critical',
    color: '#EF4444',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/30',
    textColor: 'text-red-400',
    range: [20, 25],
    description: 'Immediate action required',
  },
  HIGH: {
    value: 'High',
    label: 'High',
    color: '#F97316',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/30',
    textColor: 'text-orange-400',
    range: [15, 19],
    description: 'Senior management attention needed',
  },
  MEDIUM: {
    value: 'Medium',
    label: 'Medium',
    color: '#EAB308',
    bgColor: 'bg-yellow-500/10',
    borderColor: 'border-yellow-500/30',
    textColor: 'text-yellow-400',
    range: [9, 14],
    description: 'Management attention required',
  },
  LOW: {
    value: 'Low',
    label: 'Low',
    color: '#22C55E',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/30',
    textColor: 'text-green-400',
    range: [5, 8],
    description: 'Monitor and review',
  },
  VERY_LOW: {
    value: 'Very Low',
    label: 'Very Low',
    color: '#3B82F6',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    textColor: 'text-blue-400',
    range: [1, 4],
    description: 'Acceptable risk level',
  },
};

/**
 * Likelihood Scale (1-5)
 */
export const LIKELIHOOD_SCALE = [
  { value: 1, label: 'Rare', description: 'May occur only in exceptional circumstances (< 5%)' },
  { value: 2, label: 'Unlikely', description: 'Could occur at some time (5-25%)' },
  { value: 3, label: 'Possible', description: 'Might occur at some time (25-50%)' },
  { value: 4, label: 'Likely', description: 'Will probably occur in most circumstances (50-75%)' },
  {
    value: 5,
    label: 'Almost Certain',
    description: 'Expected to occur in most circumstances (> 75%)',
  },
];

/**
 * Consequence Scale (1-5) — ISO 31000:2018 §3.6
 * Exported as both CONSEQUENCE_SCALE (canonical) and IMPACT_SCALE (backward compat).
 */
export const CONSEQUENCE_SCALE = [
  { value: 1, label: 'Insignificant', description: 'Minimal consequence, negligible effects' },
  {
    value: 2,
    label: 'Minor',
    description: 'Limited consequence, manageable with normal procedures',
  },
  {
    value: 3,
    label: 'Moderate',
    description: 'Material consequence, requires management intervention',
  },
  {
    value: 4,
    label: 'Major',
    description: 'Significant consequence, requires senior management attention',
  },
  {
    value: 5,
    label: 'Catastrophic',
    description: 'Critical consequence, threatens organizational objectives',
  },
];

/** @deprecated Use CONSEQUENCE_SCALE — kept for backward compatibility */
export const IMPACT_SCALE = [
  { value: 1, label: 'Insignificant', description: 'Minimal impact, negligible consequences' },
  { value: 2, label: 'Minor', description: 'Limited impact, manageable with normal procedures' },
  { value: 3, label: 'Moderate', description: 'Material impact, requires management intervention' },
  {
    value: 4,
    label: 'Major',
    description: 'Significant impact, requires senior management attention',
  },
  {
    value: 5,
    label: 'Catastrophic',
    description: 'Critical impact, threatens organizational objectives',
  },
];

/**
 * Risk Status Options
 */
export const RISK_STATUSES = [
  { value: 'draft', label: 'Draft', color: '#9CA3AF', icon: 'FileText' },
  { value: 'identified', label: 'Identified', color: '#EF4444', icon: 'AlertCircle' },
  { value: 'assessed', label: 'Assessed', color: '#EAB308', icon: 'ClipboardCheck' },
  { value: 'treated', label: 'Treated', color: '#F59E0B', icon: 'ShieldCheck' },
  { value: 'monitored', label: 'Monitored', color: '#3B82F6', icon: 'Eye' },
  { value: 'closed', label: 'Closed', color: '#6B7280', icon: 'XCircle' },
];

/**
 * Treatment Types
 */
export const TREATMENT_TYPES = [
  {
    value: 'avoid',
    label: 'Avoid',
    description: 'Eliminate the risk by not proceeding',
    icon: 'XCircle',
    color: '#EF4444',
  },
  {
    value: 'reduce',
    label: 'Reduce',
    description: 'Reduce likelihood or consequence (ISO 31000)',
    icon: 'ShieldCheck',
    color: '#3B82F6',
  },
  {
    value: 'share',
    label: 'Share',
    description: 'Share risk with third party — insurance, outsourcing (ISO 31000)',
    icon: 'ArrowRightCircle',
    color: '#8B5CF6',
  },
  {
    value: 'retain',
    label: 'Retain',
    description: 'Retain and monitor the risk (ISO 31000)',
    icon: 'CheckCircle',
    color: '#10B981',
  },
];

/**
 * Treatment Status Options
 */
export const TREATMENT_STATUSES = [
  { value: 'planned', label: 'Planned', color: '#6B7280', icon: 'Calendar' },
  { value: 'in_progress', label: 'In Progress', color: '#3B82F6', icon: 'Activity' },
  { value: 'completed', label: 'Completed', color: '#10B981', icon: 'CheckCircle' },
  { value: 'deferred', label: 'Deferred', color: '#8B5CF6', icon: 'Clock' },
  { value: 'cancelled', label: 'Cancelled', color: '#EF4444', icon: 'XCircle' },
];

/**
 * Assessment Types
 */
export const ASSESSMENT_TYPES = [
  { value: 'Initial', label: 'Initial Assessment', description: 'First-time risk identification' },
  { value: 'Periodic', label: 'Periodic Review', description: 'Scheduled periodic review' },
  { value: 'Ad-hoc', label: 'Ad-hoc Assessment', description: 'Unscheduled assessment' },
  { value: 'Pre-Project', label: 'Pre-Project', description: 'Before project initiation' },
  { value: 'Post-Incident', label: 'Post-Incident', description: 'After incident occurrence' },
];

/**
 * Assessment Status Options
 */
export const ASSESSMENT_STATUSES = [
  { value: 'Planning', label: 'Planning', color: '#6B7280' },
  { value: 'In Progress', label: 'In Progress', color: '#3B82F6' },
  { value: 'Under Review', label: 'Under Review', color: '#F59E0B' },
  { value: 'Completed', label: 'Completed', color: '#10B981' },
  { value: 'Cancelled', label: 'Cancelled', color: '#EF4444' },
];

/**
 * Default Risk Filters
 */
export const DEFAULT_RISK_FILTERS = {
  status: 'all',
  classification: 'all',
  risk_level: 'all',
  owner: null,
  department: null,
  search: '',
  identified_from: null,
  identified_to: null,
};

/**
 * Risk Review Frequency (days)
 */
export const REVIEW_FREQUENCIES = {
  CRITICAL: 30, // Monthly
  HIGH: 90, // Quarterly
  MEDIUM: 180, // Semi-annually
  LOW: 365, // Annually
  VERY_LOW: 365, // Annually
};

/**
 * Risk Appetite Thresholds
 * Organizations can customize these values
 */
export const RISK_APPETITE = {
  MAX_ACCEPTABLE_SCORE: 12, // Maximum acceptable inherent risk score
  MAX_CRITICAL_RISKS: 5, // Maximum number of critical risks
  MAX_HIGH_RISKS: 20, // Maximum number of high risks
  TARGET_RESIDUAL_REDUCTION: 0.5, // Target 50% reduction in residual risk
};

/**
 * Export Options for Risk Reports
 */
export const EXPORT_FORMATS = [
  { value: 'pdf', label: 'PDF Report', icon: 'FileText' },
  { value: 'excel', label: 'Excel Spreadsheet', icon: 'FileSpreadsheet' },
  { value: 'csv', label: 'CSV Data', icon: 'Table' },
  { value: 'json', label: 'JSON Data', icon: 'Code' },
];

/**
 * Risk Heat Map Configuration
 */
export const HEAT_MAP_CONFIG = {
  LIKELIHOOD_AXIS: [1, 2, 3, 4, 5],
  IMPACT_AXIS: [1, 2, 3, 4, 5],
  CELL_SIZE: 80, // pixels
  CELL_PADDING: 4, // pixels
  SHOW_COUNT: true,
  SHOW_TOOLTIPS: true,
};

/**
 * Risk Pagination
 */
export const RISK_PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
  MAX_PAGE_SIZE: 100,
};

/**
 * Risk Sorting Options
 */
export const RISK_SORT_OPTIONS = [
  {
    value: 'risk_score_desc',
    label: 'Risk Score (High to Low)',
    field: 'risk_score',
    order: 'desc',
  },
  { value: 'risk_score_asc', label: 'Risk Score (Low to High)', field: 'risk_score', order: 'asc' },
  { value: 'modified_desc', label: 'Recently Modified', field: 'modified', order: 'desc' },
  { value: 'modified_asc', label: 'Oldest Modified', field: 'modified', order: 'asc' },
  { value: 'title_asc', label: 'Title (A-Z)', field: 'title', order: 'asc' },
  { value: 'title_desc', label: 'Title (Z-A)', field: 'title', order: 'desc' },
  { value: 'owner_asc', label: 'Owner (A-Z)', field: 'owner_name', order: 'asc' },
];
